import type { ChangeEvent, KeyboardEvent } from 'react';
import { useEffect, useState, useRef } from 'react';
import { Button, Switch, Input, message, Modal, Tooltip } from 'antd';
// import { Switch,  } from 'antd'; // Or your specific UI library

import styles from '@/styles/ChatWindow.module.scss';
import {
  exportInquiryCSV,
  getAllDocumentIdsFromFolder,
  getDocumentLinks,
  getInquiryById,
  getInquiryFilesStatus,
  getInquiryHistory,
  postChat,
  removeInquiryFile,
} from '@/services/api/chatService';
import { InquiryFileStatus } from '@/types/chat/chat';
import { generateRandomKey } from '@/utils/random';
import { ChatAttachment } from './ChatAttachment';
import InquiryFiles from './InquiryFiles';
import ModalUpdateSystemPrompt from '../Modals/ModalUpdateSystemPrompt';
import { ArrowUpOutlined, ToolOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { normalizeLocale } from '@/utils/locale';
import DisplayMessages from './ChatScreen';
import useMomentWithTimezone from '@/hooks/timezone';
import { HttpStatusCode } from 'axios';
import {
  useHandleChatMode,
  chatModeType,
} from '../hooks/useHandleChatThinkingMode';

let isFirstLoad = true;

const INQUIRY_FILES_POLL_INTERVAL = 30000;

const isFileProcessingTerminal = (status: InquiryFileStatus['status']) =>
  status === 'finished_processing' || status === 'failed';

const ChatWindow = ({ selectedInquiryId, reloadData }: any) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const momentWithTimezone = useMomentWithTimezone();
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<any>([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState<any>();
  const [reloadInquiry, setReloadInquiry] = useState<boolean>(false);
  const [documentIds, setDocumentIds] = useState<string[]>([]);
  const [openSpModal, setOpenSpModal] = useState<boolean>(false);
  const [documentLinks, setDocumentLinks] = useState<any>([]);
  const [inquiryFiles, setInquiryFiles] = useState<InquiryFileStatus[]>([]);
  const [inquiryFilesLoading, setInquiryFilesLoading] = useState(false);
  const [removingFileId, setRemovingFileId] = useState<string | null>(null);
  const [uploadFailedModalOpen, setUploadFailedModalOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const inquiryFilesPollingRef = useRef<(() => void) | null>(null);
  const pollingCleanupRef = useRef<(() => void) | null>(null);
  const documentLinksPollingRef = useRef<(() => void) | null>(null);
  const inquiryStatusPollingRef = useRef<(() => void) | null>(null);
  const { chatMode, applyMode, getPersistedMode, handleSetChatMode } = useHandleChatMode({
    defaultChatMode: 'standard',
  });
  const latestChatInquiryIdRef = useRef<string | null>(null);
  const previousInquiryIdRef = useRef<string | null>(null);
  const isSendingRef = useRef(false);

  useEffect(() => {
    latestChatInquiryIdRef.current = selectedInquiryId;
    const isNewInquiry = previousInquiryIdRef.current !== selectedInquiryId;
    const fetchInquiryData = async () => {
      if (selectedInquiryId) {
        if (isFirstLoad) setIsSending(true);

        // Reset document IDs immediately when switching to a different inquiry
        // to prevent stale IDs from a previous chat being sent to the backend.
        if (isNewInquiry) {
          setDocumentIds([]);
        }

        const inquiryInfo: any = await getInquiryById(selectedInquiryId);

        // Only sync mode when switching to a genuinely different inquiry
        // (not when reloadInquiry fires after a file-selection change).
        if (isNewInquiry) {
          // User's explicit localStorage choice always wins; fall back to the
          // API's chat_mode, then 'standard' as the final default.
          const persisted = getPersistedMode(selectedInquiryId);
          const resolvedMode = persisted ?? (inquiryInfo.data?.chat_mode || 'standard');
          applyMode(resolvedMode);
        }

        if (inquiryInfo.success) {
          setSelectedInquiry(inquiryInfo.data);
        }

        previousInquiryIdRef.current = selectedInquiryId;

        if (isFirstLoad) setIsSending(false);
        isFirstLoad = false;
      }
    };
    fetchInquiryData();
  }, [selectedInquiryId, reloadData, reloadInquiry]);

  const reloadLink = async (documentIds: any) => {
    if (!documentIds) return;

    const documentIdsArray = Array.isArray(documentIds)
      ? documentIds
      : documentIds && typeof documentIds === 'object'
        ? Object.values(documentIds).filter(Boolean)
        : [];

    if (documentIdsArray.length === 0) return;

    const existingIds = new Set(documentLinks?.map((item: any) => item.id));

    const missingIds = documentIdsArray.filter(
      (id: any) => !existingIds.has(id),
    );

    if (!missingIds.length) return;

    const formData = new FormData();
    missingIds.forEach((id: any) => {
      formData.append('document_ids', id);
    });
    const response = await getDocumentLinks(selectedInquiryId, formData);
    if (!response.success) {
      return;
    }
    setDocumentLinks((prev: any) => [...prev, ...response.data]);
  };

  const loadDocumentIds = async (
    inquiry: any,
    inquiryIdAtCallTime: string,
    fileIds: string[],
  ) => {
    if (inquiry) {
      const attachmentIds = inquiry.attachments?.map(
        (attachment: any) => attachment.id,
      ) ?? [];
      const attachmentInfo =
        await getAllDocumentIdsFromFolder(inquiryIdAtCallTime);
      if (!attachmentInfo.success) {
        return;
      }
      // Guard against stale updates: only apply if the user hasn't switched
      // to a different inquiry while the async call was in flight.
      if (latestChatInquiryIdRef.current === inquiryIdAtCallTime) {
        setDocumentIds([...attachmentInfo.data, ...attachmentIds, ...fileIds]);
      }
    }
  };

  useEffect(() => {
    if (!selectedInquiry || !selectedInquiryId) return;
    loadDocumentIds(
      selectedInquiry,
      selectedInquiryId,
      inquiryFiles.map(file => file.id),
    );
  }, [selectedInquiry, selectedInquiryId, inquiryFiles]);

  useEffect(() => {
    let isCancelled = false;
    let timeoutId: NodeJS.Timeout | null = null;

    const clearPolling = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const fetchInquiryFiles = async (isInitial: boolean) => {
      if (!selectedInquiryId || isCancelled) return;
      if (isInitial) setInquiryFilesLoading(true);

      const response = await getInquiryFilesStatus(selectedInquiryId);

      if (isCancelled) return;
      if (isInitial) setInquiryFilesLoading(false);

      if (!response.success) {
        if (isInitial) {
          setUploadFailedModalOpen(true);
        }
        return;
      }

      if (!response.data) return;

      setInquiryFiles(response.data);

      const hasPending = response.data.some(
        file => !isFileProcessingTerminal(file.status),
      );
      if (hasPending && !isCancelled) {
        timeoutId = setTimeout(
          () => fetchInquiryFiles(false),
          INQUIRY_FILES_POLL_INTERVAL,
        );
      }
    };

    if (inquiryFilesPollingRef.current) {
      inquiryFilesPollingRef.current();
      inquiryFilesPollingRef.current = null;
    }

    if (selectedInquiryId) {
      setInquiryFiles([]);
      setUploadFailedModalOpen(false);
      fetchInquiryFiles(true);
    }

    inquiryFilesPollingRef.current = () => {
      isCancelled = true;
      clearPolling();
    };

    return () => {
      isCancelled = true;
      clearPolling();
    };
  }, [selectedInquiryId, reloadData, reloadInquiry]);

  const handleRemoveInquiryFile = async (fileId: string) => {
    if (!selectedInquiryId) return;

    setRemovingFileId(fileId);
    const response = await removeInquiryFile(selectedInquiryId, fileId);
    setRemovingFileId(null);

    if (!response.success) {
      messageApi.error(response.error || t('home.chat.files.removeError'));
      return;
    }

    setInquiryFiles(prev => prev.filter(file => file.id !== fileId));
    messageApi.success(t('home.chat.files.removeSuccess'));
  };

  const handleSend = async () => {
    if (isSendingRef.current || !inputValue.trim() || !selectedInquiryId) return;

    const hasFolders = selectedInquiry?.folders && selectedInquiry.folders.length > 0;
    const hasAttachments = selectedInquiry?.attachments && selectedInquiry.attachments.length > 0;
    const hasFiles = selectedInquiry?.files && selectedInquiry.files.length > 0;

    if (chatMode !== 'chat_only' && !hasFolders && !hasAttachments && !hasFiles) {
      messageApi.warning(t('home.chat.noFilesSelectedError'));
      return;
    }

    isSendingRef.current = true;
    if (inputValue.trim()) {
      let messages = {
        id: generateRandomKey(),
        username: t('home.chat.you'),
        message: inputValue,
        created_at: momentWithTimezone(),
        updated_at: momentWithTimezone(),
        response: '',
        document_ids: {},
      };

      setMessages((prev: any) => [messages, ...prev]);
      setInputValue('');

      setIsSending(true);
      let inquiry_id = selectedInquiryId;

      if (inquiryStatusPollingRef.current) {
        inquiryStatusPollingRef.current();
      }
      inquiryStatusPollingRef.current = pollForInquiryStatus();

      const response = await postChat(
        {
          query: inputValue,
          thinking_mode: chatMode === 'thinking',
          comparison_mode: chatMode === 'comparison',
          standard_mode: chatMode === 'standard',
          chat_only_mode: chatMode === 'chat_only',
          document_ids: documentIds,
          system_language: normalizeLocale(locale),
        },
        { inquiry_id },
      );

      if (!response.success) {
        if (inquiryStatusPollingRef.current) {
          inquiryStatusPollingRef.current();
          inquiryStatusPollingRef.current = null;
        }
        messageApi.error(response?.error);

        setIsSending(false);
        isSendingRef.current = false;
        setMessages((prev: any) => prev.slice(1));
        if (response?.status == HttpStatusCode.PayloadTooLarge) {
          messageApi.error(t('home.chat.errors.inputLimit'));
        }
        return;
      }
      const botResponse = response.data as any;

      const documentIdsArray = Array.isArray(botResponse.document_ids)
        ? botResponse.document_ids
        : botResponse.document_ids &&
          typeof botResponse.document_ids === 'object'
          ? Object.values(botResponse.document_ids).filter(Boolean)
          : [];

      if (documentIdsArray.length > 0) {
        reloadLink(documentIdsArray);
      }

      const isReferenceGenerated = botResponse.is_reference_generated === true;

      messages = {
        id: botResponse.id,
        username: botResponse.sender_id,
        message: botResponse.message,
        response: botResponse.response,
        document_ids: botResponse.document_ids,
        created_at: botResponse.created_at,
        updated_at: botResponse.updated_at,
        status: botResponse.status,
        waitingForReferences: !isReferenceGenerated,
        mode: botResponse.mode,
      } as any;
      setIsSending(false);
      isSendingRef.current = false;
      if (
        Number(latestChatInquiryIdRef.current) ===
        Number(botResponse.inquiry_id)
      ) {
        setMessages((prev: any) => [messages, ...prev.slice(1)]);
      }

      if (inquiryStatusPollingRef.current) {
        inquiryStatusPollingRef.current();
        inquiryStatusPollingRef.current = null;
      }

      if (pollingCleanupRef.current) {
        pollingCleanupRef.current();
      }
      pollingCleanupRef.current = pollForUpdatedMessage(
        botResponse.id,
        botResponse.response,
      );

      if (!isReferenceGenerated) {
        if (documentLinksPollingRef.current) {
          documentLinksPollingRef.current();
        }
        documentLinksPollingRef.current = pollForDocumentLinks(
          botResponse.id,
          documentIdsArray,
        );
      }
    }
  };

  useEffect(() => {
    return () => {
      if (pollingCleanupRef.current) {
        pollingCleanupRef.current();
      }
      if (documentLinksPollingRef.current) {
        documentLinksPollingRef.current();
      }
      if (inquiryStatusPollingRef.current) {
        inquiryStatusPollingRef.current();
      }
      if (inquiryFilesPollingRef.current) {
        inquiryFilesPollingRef.current();
      }
    };
  }, []);

  const pollForDocumentLinks = (messageId: string, documentIds: any[]) => {
    const pollInterval = 20000;
    const maxAttempts = 15;
    const maxConsecutiveErrors = 3;
    let attempts = 0;
    let consecutiveErrors = 0;
    let timeoutId: NodeJS.Timeout | null = null;
    let isCancelled = false;

    const poll = async () => {
      if (attempts >= maxAttempts || isCancelled || !selectedInquiryId) {
        setMessages((prev: any) =>
          prev.map((msg: any) =>
            msg.id === messageId
              ? {
                ...msg,
                waitingForReferences: false,
                referenceError:
                  attempts >= maxAttempts
                    ? t('home.chat.errors.referenceTimeout')
                    : msg.referenceError,
              }
              : msg,
          ),
        );
        if (attempts >= maxAttempts) {
          messageApi.error(t('home.chat.errors.referenceTimeout'));
        }
        return;
      }

      attempts++;

      try {
        const historyResponse = await getInquiryHistory(selectedInquiryId, {
          size: 20,
        });

        if (isCancelled) return;

        if (!historyResponse.success) {
          consecutiveErrors++;
          const errorMessage =
            (historyResponse as any).error ||
            t('home.chat.errors.referenceLoadFailed');
          setMessages((prev: any) =>
            prev.map((msg: any) =>
              msg.id === messageId
                ? { ...msg, referenceError: errorMessage }
                : msg,
            ),
          );

          if (consecutiveErrors >= maxConsecutiveErrors) {
            setMessages((prev: any) =>
              prev.map((msg: any) =>
                msg.id === messageId
                  ? {
                    ...msg,
                    waitingForReferences: false,
                    referenceError: errorMessage,
                  }
                  : msg,
              ),
            );
            messageApi.error(t('home.chat.errors.referenceLoadFailed') + ': ' + errorMessage);
            return;
          }

          if (attempts < maxAttempts && !isCancelled) {
            timeoutId = setTimeout(poll, pollInterval);
          }
          return;
        }

        consecutiveErrors = 0;

        if (historyResponse.success && historyResponse.data) {
          const updatedMessage = historyResponse.data.find(
            (msg: any) => msg.id === messageId,
          );

          if (updatedMessage) {
            const isReferenceGenerated =
              updatedMessage.is_reference_generated === true;

            if (isReferenceGenerated) {
              setMessages((prev: any) =>
                prev.map((msg: any) =>
                  msg.id === messageId
                    ? {
                      ...msg,
                      waitingForReferences: false,
                      referenceError: null,
                    }
                    : msg,
                ),
              );
              return;
            }

            const documentIdsArray = Array.isArray(updatedMessage.document_ids)
              ? updatedMessage.document_ids
              : updatedMessage.document_ids &&
                typeof updatedMessage.document_ids === 'object'
                ? Object.values(updatedMessage.document_ids).filter(Boolean)
                : [];

            if (documentIdsArray.length > 0) {
              const formData = new FormData();
              documentIdsArray.forEach((id: any) => {
                formData.append('document_ids', id);
              });

              const response = await getDocumentLinks(
                selectedInquiryId,
                formData,
              );

              if (isCancelled) return;

              if (!response.success) {
                consecutiveErrors++;
                const errorMessage =
                  response.error || t('home.chat.errors.referenceLoadFailed');
                setMessages((prev: any) =>
                  prev.map((msg: any) =>
                    msg.id === messageId
                      ? { ...msg, referenceError: errorMessage }
                      : msg,
                  ),
                );

                if (consecutiveErrors >= maxConsecutiveErrors) {
                  setMessages((prev: any) =>
                    prev.map((msg: any) =>
                      msg.id === messageId
                        ? {
                          ...msg,
                          waitingForReferences: false,
                          referenceError: errorMessage,
                        }
                        : msg,
                    ),
                  );
                  messageApi.error(t('home.chat.errors.referenceLoadFailed') + ': ' + errorMessage);
                  return;
                }

                if (attempts < maxAttempts && !isCancelled) {
                  timeoutId = setTimeout(poll, pollInterval);
                }
                return;
              }

              consecutiveErrors = 0;

              if (
                response.success &&
                response.data &&
                response.data.length > 0
              ) {
                setDocumentLinks((prev: any) => {
                  const existingIds = new Set(
                    prev?.map((item: any) => item.id) || [],
                  );
                  const newLinks = response.data.filter(
                    (link: any) => !existingIds.has(link.id),
                  );
                  return [...prev, ...newLinks];
                });
              }
            } else {
              if (attempts >= maxAttempts) {
                const errorMessage = t('home.chat.errors.referenceTimeout');
                setMessages((prev: any) =>
                  prev.map((msg: any) =>
                    msg.id === messageId
                      ? {
                        ...msg,
                        waitingForReferences: false,
                        referenceError: errorMessage,
                      }
                      : msg,
                  ),
                );
                messageApi.error(t('home.chat.errors.referenceTimeout'));
                return;
              }
            }
          } else {
            if (attempts >= maxAttempts) {
              const errorMessage = t('home.chat.errors.referenceTimeout');
              setMessages((prev: any) =>
                prev.map((msg: any) =>
                  msg.id === messageId
                    ? {
                      ...msg,
                      waitingForReferences: false,
                      referenceError: errorMessage,
                    }
                    : msg,
                ),
              );
              messageApi.error(t('home.chat.errors.referenceTimeout'));
              return;
            }
          }
        }

        if (attempts < maxAttempts && !isCancelled) {
          timeoutId = setTimeout(poll, pollInterval);
        }
      } catch (error: any) {
        consecutiveErrors++;
        const errorMessage =
          error.message || t('home.chat.errors.referenceLoadFailed');
        setMessages((prev: any) =>
          prev.map((msg: any) =>
            msg.id === messageId
              ? { ...msg, referenceError: errorMessage }
              : msg,
          ),
        );

        if (consecutiveErrors >= maxConsecutiveErrors) {
          setMessages((prev: any) =>
            prev.map((msg: any) =>
              msg.id === messageId
                ? {
                  ...msg,
                  waitingForReferences: false,
                  referenceError: errorMessage,
                }
                : msg,
            ),
          );
          messageApi.error(t('home.chat.errors.referenceLoadFailed'));
          return;
        }

        if (attempts < maxAttempts && !isCancelled) {
          timeoutId = setTimeout(poll, pollInterval);
        }
      }
    };

    timeoutId = setTimeout(poll, pollInterval);

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  };

  const pollForInquiryStatus = () => {
    const pollInterval = 5000;
    let timeoutId: NodeJS.Timeout | null = null;
    let isCancelled = false;

    const poll = async () => {
      if (isCancelled || !selectedInquiryId) {
        return;
      }

      try {
        const response = await getInquiryHistory(selectedInquiryId, {
          size: 20,
        });

        if (isCancelled) return;

        if (response.success && response.data && response.data.length > 0) {
          const latestMessage = response.data[0];
          setMessages((prev: any) => {
            if (isCancelled) return prev;
            const loadingBotMessage = prev.find(
              (msg: any) =>
                (msg.isSender === false || !msg.isSender) &&
                (!msg.response || msg.response === ''),
            );

            if (
              loadingBotMessage &&
              !latestMessage.response &&
              latestMessage.status
            ) {
              return prev.map((msg: any) =>
                msg.id === loadingBotMessage.id
                  ? { ...msg, status: latestMessage.status }
                  : msg,
              );
            }

            return prev;
          });
        }
      } catch (error) {
        // Silently handle errors, just continue polling
      }

      if (!isCancelled) {
        timeoutId = setTimeout(poll, pollInterval);
      }
    };

    poll();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  };

  const pollForUpdatedMessage = (
    messageId: string,
    initialResponse: string = '',
  ) => {
    const maxAttempts = 10;
    const pollInterval = 10000;
    let attempts = 0;
    let timeoutId: NodeJS.Timeout | null = null;
    let isCancelled = false;
    let lastResponse = initialResponse;

    const poll = async () => {
      if (attempts >= maxAttempts || isCancelled || !selectedInquiryId) {
        return;
      }

      attempts++;

      try {
        const response = await getInquiryHistory(selectedInquiryId, {
          size: 20,
        });

        if (isCancelled) return;

        if (response.success && response.data) {
          const updatedMessage = response.data.find(
            (msg: any) => msg.id === messageId,
          );

          if (updatedMessage) {
            const documentIdsArray = Array.isArray(updatedMessage.document_ids)
              ? updatedMessage.document_ids
              : updatedMessage.document_ids &&
                typeof updatedMessage.document_ids === 'object'
                ? Object.values(updatedMessage.document_ids).filter(Boolean)
                : [];

            const hasReferences = documentIdsArray.length > 0;
            const isReferenceGenerated =
              updatedMessage.is_reference_generated === true;
            const responseChanged = updatedMessage.response !== lastResponse;

            setMessages((prev: any) => {
              if (isCancelled) return prev;

              const currentMessage = prev.find(
                (msg: any) => msg.id === messageId,
              );
              if (!currentMessage) return prev;

              const currentDocumentIdsArray = Array.isArray(
                currentMessage?.document_ids,
              )
                ? currentMessage.document_ids
                : currentMessage?.document_ids &&
                  typeof currentMessage?.document_ids === 'object'
                  ? Object.values(currentMessage.document_ids).filter(Boolean)
                  : [];

              const currentHasReferences = currentDocumentIdsArray.length > 0;
              const currentResponseChanged =
                updatedMessage.response !== currentMessage?.response;
              const referencesChanged =
                hasReferences !== currentHasReferences ||
                documentIdsArray.length !== currentDocumentIdsArray.length;

              if (currentResponseChanged || referencesChanged) {
                if (
                  hasReferences &&
                  referencesChanged &&
                  !isReferenceGenerated
                ) {
                  reloadLink(documentIdsArray);
                  if (documentLinksPollingRef.current) {
                    documentLinksPollingRef.current();
                  }
                  documentLinksPollingRef.current = pollForDocumentLinks(
                    messageId,
                    documentIdsArray,
                  );
                } else if (
                  isReferenceGenerated &&
                  documentLinksPollingRef.current
                ) {
                  documentLinksPollingRef.current();
                  documentLinksPollingRef.current = null;
                }
                const updatedMessages = prev.map((msg: any) =>
                  msg.id === messageId
                    ? {
                      ...msg,
                      ...updatedMessage,
                      document_ids: documentIdsArray,
                      response: updatedMessage.response,
                      waitingForReferences:
                        !isReferenceGenerated &&
                        hasReferences &&
                        documentIdsArray.length > 0,
                    }
                    : msg,
                );
                return updatedMessages;
              }
              return prev;
            });

            lastResponse = updatedMessage.response;

            if (isReferenceGenerated || (hasReferences && !responseChanged)) {
              return;
            }
          }
        }

        if (attempts < maxAttempts && !isCancelled) {
          timeoutId = setTimeout(poll, pollInterval);
        }
      } catch (error) {
        if (attempts < maxAttempts && !isCancelled) {
          timeoutId = setTimeout(poll, pollInterval);
        }
      }
    };

    timeoutId = setTimeout(poll, pollInterval);

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  };

  const handleCSVExport = async () => {
    if (!selectedInquiryId) return;

    const { success, error } = await exportInquiryCSV(
      selectedInquiryId,
      normalizeLocale(locale),
    );
    if (!success) {
      messageApi.error(t('home.chat.csvExport.error'));
    }
  };

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipOpen2, setTooltipOpen2] = useState(false);
  const [tooltipOpen3, setTooltipOpen3] = useState(false);
  const [tooltipOpen4, setTooltipOpen4] = useState(false);

  useEffect(() => {
    // Reset all tooltips first
    setTooltipOpen(false);
    setTooltipOpen2(false);
    setTooltipOpen3(false);
    setTooltipOpen4(false);

    let timer: NodeJS.Timeout;

    if (chatMode === 'comparison') {
      setTooltipOpen(true);
      timer = setTimeout(() => {
        setTooltipOpen(false);
      }, 3000);
    } else if (chatMode === 'thinking') {
      setTooltipOpen2(true);
      timer = setTimeout(() => {
        setTooltipOpen2(false);
      }, 3000);
    } else if (chatMode === 'standard') {
      setTooltipOpen3(true);
      timer = setTimeout(() => {
        setTooltipOpen3(false);
      }, 3000);
    } else if (chatMode === 'chat_only') {
      setTooltipOpen4(true);
      timer = setTimeout(() => {
        setTooltipOpen4(false);
      }, 3000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [chatMode]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {selectedInquiryId && (
          <Button
            className={styles.prompt}
            onClick={handleCSVExport}
            style={{ marginRight: '8px' }}
          >
            {t('home.chat.csvExport.button')}
          </Button>
        )}
        <Button className={styles.prompt} onClick={() => setOpenSpModal(true)}>
          <ToolOutlined />
          <span>{t('home.chat.prompt')}</span>
        </Button>
      </div>
      <DisplayMessages
        selectedInquiryId={selectedInquiryId}
        messages={messages}
        setMessages={setMessages}
        documentLinks={documentLinks}
        setDocumentLinks={setDocumentLinks}
      />
      <div className={styles.inputContainer}>
        {selectedInquiryId && (
          <InquiryFiles
            inquiryId={selectedInquiryId}
            files={inquiryFiles}
            loading={inquiryFilesLoading}
            removingId={removingFileId}
            onRemove={handleRemoveInquiryFile}
          />
        )}
        {selectedInquiryId && (
          <ChatAttachment
            inquiryId={selectedInquiryId}
            inquiry={selectedInquiry}
            setReloadInquiry={setReloadInquiry}
            reloadData={reloadData}
          >
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Tooltip
                  overlayStyle={{ maxWidth: '500px' }}
                  title={
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {t('home.chat.thinkingModeToolTip')}
                    </div>
                  }
                  open={tooltipOpen2} // <--- CHANGED THIS
                  placement="top"
                >
                  <Switch
                    checked={chatMode === 'thinking'}
                    onChange={(checked: boolean) => {
                      handleSetChatMode(checked ? 'thinking' : 'standard', selectedInquiryId);
                    }}
                  />
                </Tooltip>
                <span>{t('home.chat.thinkingMode')}</span>
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Tooltip
                  overlayStyle={{ maxWidth: '500px' }}
                  title={
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {t('home.chat.comparisonModeToolTip')}
                    </div>
                  }
                  open={tooltipOpen} // <--- CHANGED THIS
                  placement="top"
                >
                  <Switch
                    checked={chatMode === 'comparison'}
                    onChange={(checked: boolean) => {
                      handleSetChatMode(checked ? 'comparison' : 'standard', selectedInquiryId);
                    }}
                  />
                </Tooltip>
                <span>{t('home.chat.comparisonMode')}</span>
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Tooltip
                  overlayStyle={{ maxWidth: '500px' }}
                  title={
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {t('home.chat.standardModeToolTip')}
                    </div>
                  }
                  open={tooltipOpen3}
                  placement="top"
                >
                  <Switch
                    checked={chatMode === 'standard'}
                    onChange={(checked: boolean) => {
                      if (!checked) return;
                      handleSetChatMode('standard', selectedInquiryId);
                    }}
                  />
                </Tooltip>
                <span>{t('home.chat.standardMode')}</span>
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Tooltip
                  overlayStyle={{ maxWidth: '500px' }}
                  title={
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {t('home.chat.chatOnlyModeToolTip')}
                    </div>
                  }
                  open={tooltipOpen4}
                  placement="top"
                >
                  <Switch
                    checked={chatMode === 'chat_only'}
                    onChange={(checked: boolean) => {
                      if (!checked) return;
                      handleSetChatMode('chat_only', selectedInquiryId);
                    }}
                  />
                </Tooltip>
                <span>{t('home.chat.chatOnlyMode')}</span>
              </div>
            </div>
          </ChatAttachment>
        )}

        <div className={styles.inputChat}>
          <Input.TextArea
            className={styles.input}
            placeholder={t('home.chat.placeholder')}
            value={inputValue}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setInputValue(e.target.value)
            }
            onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={3}
          />
          <Button
            className={styles.sendButton}
            type="primary"
            onClick={handleSend}
            disabled={isSending || !selectedInquiryId || !inputValue}
          >
            {isSending ? (
              <div
                style={{ width: '10px', height: '10px', background: 'white' }}
              />
            ) : (
              <ArrowUpOutlined style={{ fontWeight: 'bold' }} />
            )}
          </Button>
        </div>
      </div>
      <ModalUpdateSystemPrompt
        inquiry={selectedInquiry}
        open={openSpModal}
        setSelectedInquiry={setSelectedInquiry}
        onCancel={() => {
          setOpenSpModal(false);
        }}
      />
      <Modal
        open={uploadFailedModalOpen}
        title={t('home.chat.files.uploadFailedTitle')}
        onOk={() => setUploadFailedModalOpen(false)}
        onCancel={() => setUploadFailedModalOpen(false)}
        okText={t('home.chat.files.close')}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {t('home.chat.files.uploadFailed')}
      </Modal>
      {contextHolder}
    </div>
  );
};

export default ChatWindow;
